import EmptyState from '@/components/linear/empty-state';
import PageToolbar from '@/components/linear/page-toolbar';
import { editProjectPath, newProjectPath, projectPath, projectsPath } from '@/lib/linear-paths';
import type { Company } from '@/types/company';
import type { Project } from '@/types/project';
import { Link, router } from '@inertiajs/react';
import { FormEvent, useState } from 'react';

interface Props {
  projects: Project[];
  currentCompany: Company;
  query: string;
  scope: 'active' | 'archived' | 'all';
}

export default function Index({ projects, currentCompany, query, scope }: Props) {
  const [search, setSearch] = useState(query ?? '');

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    router.get(
      projectsPath(),
      { q: search || undefined, scope: scope === 'active' ? undefined : scope },
      { preserveState: true }
    );
  };

  const handleScopeChange = (nextScope: string) => {
    if (!nextScope) return;
    router.get(
      projectsPath(),
      {
        q: query || undefined,
        scope: nextScope === 'active' ? undefined : nextScope,
      },
      { preserveState: true }
    );
  };

  return (
    <div className="ln-page">
      <PageToolbar
        title="Projects"
        tabs={
          <div className="ln-tabs">
            <button
              type="button"
              className="ln-tab"
              data-active={scope === 'active' ? 'true' : 'false'}
              onClick={() => handleScopeChange('active')}
            >
              Active
            </button>
            <button
              type="button"
              className="ln-tab"
              data-active={scope === 'archived' ? 'true' : 'false'}
              onClick={() => handleScopeChange('archived')}
            >
              Archive
            </button>
            <button
              type="button"
              className="ln-tab"
              data-active={scope === 'all' ? 'true' : 'false'}
              onClick={() => handleScopeChange('all')}
            >
              All
            </button>
          </div>
        }
        actions={
          <>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
              <input
                className="ln-filter-input"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={`Search ${currentCompany.name}…`}
                aria-label="Search projects"
              />
            </form>
            <Link href={newProjectPath()} className="ln-primary-btn" style={{ marginLeft: 0 }}>
              New project
            </Link>
          </>
        }
        showNew={false}
      />
      <div className="ln-page-scroll">
        {projects.length === 0 ? (
          <EmptyState description="No projects match." />
        ) : (
          projects.map((project) => (
            <Link
              key={project.id}
              href={projectPath(project.id)}
              className="ln-row"
              style={{ height: 48 }}
            >
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 4,
                  background: project.archived ? '#6b6f76' : '#5e6ad2',
                  flexShrink: 0,
                }}
              />
              <span className="ln-row-title">{project.name}</span>
              {project.archived ? <span className="ln-label">Archived</span> : null}
              {project.singleShot ? <span className="ln-label">Single-shot</span> : null}
              {project.statusBlocked ? (
                <span className="ln-label" style={{ color: '#eb5757' }}>
                  Blocked
                </span>
              ) : null}
              <span className="ln-row-meta">
                <span
                  className="ln-inbox-sub"
                  style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {project.statusNow || project.endState}
                </span>
                <button
                  type="button"
                  className="ln-toolbar-btn"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    router.visit(editProjectPath(project.id));
                  }}
                >
                  Edit
                </button>
              </span>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
